
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  HomeIcon,
  AcademicCapIcon,
  BeakerIcon,
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon
} from '@heroicons/react/24/outline';
import { logoutUser } from '../api';

const Sidebar = ({ isExpanded, onExpandChange }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Handle logout functionality
  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  // Navigation menu items configuration
const navItems = [
    { name: 'Dashboard', icon: HomeIcon, path: '/dashboard' },
    { name: 'Training', icon: AcademicCapIcon, path: '/training' },
    { name: 'Testing', icon: BeakerIcon, path: '/testing' },
    { name: 'Settings', icon: Cog6ToothIcon, path: '/settings' }
  ];

  // Handles mouse events for sidebar expansion
  const handleMouseEvents = {
    onMouseEnter: () => onExpandChange(true),
    onMouseLeave: () => onExpandChange(false)
  };

  // Generates dynamic sidebar width classes
  const getSidebarWidthClass = () => 
    `fixed left-0 top-16 h-[calc(100vh-64px)] transition-all duration-300 ease-in-out z-10 ${
      isExpanded ? 'w-[260px]' : 'w-[70px]'
    }`;

  // Determines if a nav item is currently active
  const isNavItemActive = (path) => location.pathname === path;

  // Generates classes for the nav item container
  const getNavItemClass = (isActive) => 
    `relative flex items-center h-[50px] transition-all duration-200 ${
      isActive 
        ? 'text-[#4169E1] bg-blue-50' 
        : 'text-gray-500 hover:text-[#4169E1] hover:bg-blue-50'
    }`;

  // Renders the icon container with proper spacing
  const renderIconContainer = (Icon, isExpanded) => (
    <div className={`flex items-center justify-center ${isExpanded ? 'w-10 h-10' : 'w-12 h-12'} rounded-lg transition-colors`}>
      <Icon className="w-7 h-7 transition-colors stroke-[1.5]" />
    </div>
  );

  // Renders a single navigation item
  const renderNavItem = (item) => {
    const isActive = isNavItemActive(item.path);
    return (
      <Link
        key={item.name}
        to={item.path}
        className={getNavItemClass(isActive)}
      >
        <div className={`flex items-center ${isExpanded ? 'pl-6' : 'justify-center w-full'}`}>
          {renderIconContainer(item.icon, isExpanded)}
          {isExpanded && (
            <span className="ml-3 text-[15px] font-medium whitespace-nowrap transition-opacity duration-300">
              {item.name}
            </span>
          )}
        </div>
      </Link>
    );
  };

  return (
    <aside 
      className={getSidebarWidthClass()}
      {...handleMouseEvents}
    >
      <div className="h-full bg-white shadow-sm pt-10 flex flex-col">
        {/* Navigation Items */}
        <div className="flex-1">
          {navItems.map(renderNavItem)}
        </div>
        
        {/* Logout Button at Bottom */}
        <div className="pb-6">
          <button
            onClick={handleLogout}
            className="relative flex items-center h-[50px] w-full transition-all duration-200 text-gray-500 hover:text-red-600 hover:bg-red-50"
          >
            <div className={`flex items-center ${isExpanded ? 'pl-6' : 'justify-center w-full'}`}>
              <div className={`flex items-center justify-center ${isExpanded ? 'w-10 h-10' : 'w-12 h-12'} rounded-lg transition-colors`}>
                <ArrowLeftOnRectangleIcon className="w-7 h-7 transition-colors stroke-[1.5]" />
              </div>
              {isExpanded && (
                <span className="ml-3 text-[15px] font-medium whitespace-nowrap transition-opacity duration-300">
                  Logout
                </span>
              )}
            </div>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;